import { Store } from "vuex";

import { useCreatorShopPostsStore } from "@/store/creator-shop-posts";
import { getExperimentsStore } from "@/store/experiments";
import { getLtksStore } from "@/store/ltks";

import { FLAG_CREATOR_SHOP_SEARCH } from "../experiments/constants";

export interface LtkItemHeightCalculationData {
  ltkId: string;
  width: number;
}
export type LtkItemHeightCalculation = (store: Store<unknown>, data: LtkItemHeightCalculationData) => number;

const heroTitleSpacing = 8;
const heroProductsSpacing = 8;
const avatarHeroSpacing = 8;
const avatarHeight = 33;
const titleLineHeight = 26;
const creatorAvatarHeight = 32;
const creatorAvatarSpacing = 8;
const creatorLtksHeight = 160;

function productsHeight(width: number) {
  const items = 4;
  const gutters = items - 1;
  const totalGutterWidth = items * 2 * gutters;

  return (width - totalGutterWidth) / items;
}

export const heroImageOnly: LtkItemHeightCalculation = (store, { ltkId, width }): number => {
  const ltksStore = getLtksStore(store);
  const aspect = ltksStore.ltkImageAspectRatio(ltkId);
  const safeAspect = aspect > 0 && isFinite(aspect) ? aspect : 1;

  return (1 / safeAspect) * width;
};

export const heroImageCreatorShop: LtkItemHeightCalculation = (store, { ltkId, width }): number => {
  if (getExperimentsStore(store).hasFlag(FLAG_CREATOR_SHOP_SEARCH)) {
    return heroImageOnly(store, { ltkId, width });
  }

  const creatorShopPostsStore = useCreatorShopPostsStore();
  const aspect = creatorShopPostsStore.getHeroAspectRatio(ltkId);
  const safeAspect = aspect > 0 && isFinite(aspect) ? aspect : 1;

  return (1 / safeAspect) * width;
};

export const withProductsRow: LtkItemHeightCalculation = (store, data) => {
  return heroImageOnly(store, data) + productsHeight(data.width) + heroProductsSpacing;
};

export const withAvatar: LtkItemHeightCalculation = (store, data) => {
  return avatarHeight + avatarHeroSpacing + heroImageOnly(store, data);
};

export const withAvatarAndProductsRow: LtkItemHeightCalculation = (store, data) => {
  return (
    avatarHeight + avatarHeroSpacing + heroImageOnly(store, data) + productsHeight(data.width) + heroProductsSpacing
  );
};

export const giftGuideTitle: (parent: HTMLElement, titleClassName: string) => LtkItemHeightCalculation =
  (parent, titleClassName) => (store, data) => {
    const ltksStore = getLtksStore(store);
    const title = ltksStore.ltks[data.ltkId]?.title;

    if (title === undefined) {
      return 0;
    }

    // Stub out dynamic text height calculation on server side where there is no DOM to work with
    if (process.server) {
      return titleLineHeight * 2;
    }

    const fakeTitleElement = document.createElement("div");
    fakeTitleElement.className = titleClassName;
    fakeTitleElement.innerText = title;
    fakeTitleElement.style.setProperty("display", "inline-block", "important");
    fakeTitleElement.style.setProperty("width", `${data.width}px`, "important");

    // Ensure item doesn't render visibly to the end user, but will have calculated dimensiones in the document
    fakeTitleElement.style.setProperty("visibility", "hidden", "important");
    fakeTitleElement.style.setProperty("position", "absolute", "important");

    // Attempt to place in a parent element to receive any inherited stylings
    const node = parent === undefined ? document.getElementById("app") ?? document.body : parent;

    node.appendChild(fakeTitleElement);
    const height = fakeTitleElement.clientHeight;
    node.removeChild(fakeTitleElement);

    const titlePaddingDefiningLinkedPost = 8;

    return height + titlePaddingDefiningLinkedPost;
  };

export const withTitle: (parent: HTMLElement, titleClassName: string) => LtkItemHeightCalculation =
  (parent, titleClassName) => (store, data) => {
    const heroImageHeight = heroImageOnly(store, data);
    const titleHeight = giftGuideTitle(parent, titleClassName)(store, data);

    if (titleHeight === 0) {
      return heroImageHeight;
    }

    return heroImageHeight + heroTitleSpacing + titleHeight;
  };

export const withCreatorAvatarAndLtksRow: LtkItemHeightCalculation = (store, data) => {
  const ltksStore = getLtksStore(store);
  const id = data.ltkId;
  const hasNoFeed = ltksStore.ltkFeed(id).meta !== null && ltksStore.getFeedHasNoLtks(id);
  if (hasNoFeed) {
    return creatorAvatarHeight;
  }
  return creatorAvatarHeight + creatorAvatarSpacing + creatorLtksHeight;
};
